import React from 'react';
import { Button } from 'primereact/button';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { APPLICATION_STATUS, JOB_STATUS } from './careersShared';

// Layout pieces shared by the careers admin pages (styles in careers.css)

/** Page wrapper; scopes the careers styles and the global-CSS fixes. */
export function CareersPage({ children, narrow = false }) {
  return (
    <div className="body_content careers-page">
      <div className={`careers-container ${narrow ? 'is-narrow' : ''}`}>{children}</div>
    </div>
  );
}

/** Title row with an optional back link and actions on the right. */
export function PageHeader({ title, subtitle, back, actions, badge }) {
  return (
    <header className="careers-header">
      <div className="careers-header-text">
        {back && (
          <button type="button" className="careers-back" onClick={back.onClick}>
            <i className="pi pi-arrow-left" aria-hidden="true" />
            {back.label}
          </button>
        )}
        <div className="d-flex align-items-center flex-wrap gap-2">
          <h1 className="careers-title">{title}</h1>
          {badge}
        </div>
        {subtitle && <p className="careers-subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="careers-header-actions">{actions}</div>}
    </header>
  );
}

export function Panel({ title, icon, description, actions, children, className = '' }) {
  return (
    <section className={`careers-card ${className}`}>
      {(title || actions) && (
        <div className="careers-card-head">
          <div>
            {title && (
              <h2 className="careers-card-title">
                {icon && <i className={`pi ${icon}`} aria-hidden="true" />}
                {title}
              </h2>
            )}
            {description && <p className="careers-card-description">{description}</p>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export function SearchBox({ value, onChange, placeholder, label }) {
  return (
    <IconField iconPosition="left" className="careers-search">
      <InputIcon className="pi pi-search" />
      <InputText value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={label} />
    </IconField>
  );
}

export function Loading() {
  return <div className="careers-loading"><ProgressSpinner style={{ width: 40, height: 40 }} strokeWidth="4" /></div>;
}

export function EmptyState({ icon = 'pi-inbox', title, children }) {
  return (
    <div className="careers-empty">
      <span className="careers-empty-icon"><i className={`pi ${icon}`} aria-hidden="true" /></span>
      <p className="careers-empty-title">{title}</p>
      {children}
    </div>
  );
}

export function ErrorBanner({ message, onRetry }) {
  if (!message) return null;
  return (
    <div className="careers-error" role="alert">
      <i className="pi pi-exclamation-circle" aria-hidden="true" />
      <span className="flex-grow-1">{message}</span>
      {onRetry && <Button label="Retry" text size="small" onClick={onRetry} />}
    </div>
  );
}

export function StatusBadge({ status }) {
  return <span className={`careers-badge is-${status}`}>{APPLICATION_STATUS[status]?.label || status}</span>;
}

export function JobStatusBadge({ status }) {
  return <span className={`careers-badge is-job-${status}`}>{JOB_STATUS[status]?.label || status}</span>;
}

export function initials(name = '') {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?';
}

export function PersonAvatar({ name, size = 'md' }) {
  return <span className={`careers-avatar is-${size}`} aria-hidden="true">{initials(name)}</span>;
}
